<template>
  <AppShell>
    <section class="view on soa-view-files" aria-labelledby="files-title">
      <div class="pagehead">
        <div><h1 id="files-title">{{ t("files_h") }}</h1><p class="sub">{{ t("files_sub") }}</p></div>
        <button type="button" class="btn" @click="newArticle">{{ t("blog_new") }}</button>
      </div>
      <p v-if="loading" class="empty" role="status">Loading deliverables...</p>
      <p v-else-if="error" class="empty" role="alert">{{ error }} <button type="button" class="atbtn" @click="load">Retry</button></p>
      <p v-else-if="!currentBrand" class="empty">{{ t("prof_none") }}</p>
      <p v-else-if="!items.length" class="empty">{{ t("files_none_brand") }}</p>
      <div v-else class="rlog">
        <article v-for="file in pageItems" :key="file.slug" class="pl-card" :class="{ 'file-openable': true }" tabindex="0" @click="openFile($event, file)" @keydown="openFileByKey($event, file)">
          <div class="pl-top">
            <span class="pl-chip pl-m">{{ file.kind }}</span>
            <span class="pl-chip pl-bx">{{ file.brand || (lang === "zh" ? "未标品牌" : "No brand") }}</span>
            <h2>{{ file.title }}</h2>
            <time class="pl-brand" :datetime="dateTime(file.created)">{{ formatDate(file.created) }}</time>
            <button type="button" class="pl-brand file-link" aria-label="Open deliverable in Maya" @click.stop="openFile($event, file)">↗</button>
          </div>
          <div v-if="file.kind === 'article'" class="ws-pacts" @click.stop>
            <button type="button" class="ws-pact" @click="edit(file)">{{ t("blog_edit") }}</button>
            <button v-if="file.published" type="button" class="ws-pact art-publish" :disabled="busy === file.slug" @click="publish(file)">{{ busy === file.slug ? "Publishing..." : t("art_republish") }}</button>
            <button type="button" class="ws-pact del" :disabled="busy === file.slug" @click="requestDelete(file)">{{ t("ws_pg_del") }}</button>
          </div>
          <div v-if="publishedSlug === file.slug" class="art-publine" role="status">✓ {{ t(publishedWas ? "art_republished" : "art_published") }} <a v-if="publishedUrl" :href="publishedUrl" target="_blank" rel="noopener" @click.stop>{{ publishedUrl }}</a></div>
          <div v-if="confirmSlug === file.slug" class="ws-pconf" @click.stop>
            <b>{{ t("ws_bv_delq") }}</b>
            <div class="ws-pnote">{{ file.published ? t("ws_bv_delnote") : t("ws_bv_delnote_draft") }}</div>
            <div class="ws-pacts"><button type="button" class="ws-pact del" :disabled="busy === file.slug" @click="remove(file)">{{ t("ws_pg_del") }}</button><button type="button" class="ws-pact" @click="confirmSlug = null">{{ t("ws_pg_cancel") }}</button></div>
          </div>
        </article>
      </div>
      <nav v-if="pages > 1" class="list-pager" aria-label="Deliverables pagination">
        <button type="button" :disabled="page <= 1" aria-label="Previous page" @click="page--">‹</button>
        <span>{{ t("pagination_page").replace("{page}", page).replace("{total}", pages) }}</span>
        <button type="button" :disabled="page >= pages" aria-label="Next page" @click="page++">›</button>
      </nav>
      <p class="sr-only" role="status" aria-live="polite">{{ status }}</p>
    </section>
  </AppShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { deleteArticle, listDeliverables, publishDeliverable } from "../api/deliverables";
import AppShell from "../layout/AppShell.vue";
import { currentBrand } from "../stores/brands";
import { useI18n } from "../useI18n";

const files = ref([]), loading = ref(true), error = ref(""), busy = ref(null), status = ref(""), page = ref(1), confirmSlug = ref(null);
const publishedSlug = ref(null), publishedUrl = ref(""), publishedWas = ref(false);
const router = useRouter();
const { t, lang } = useI18n();
let controller;
const items = computed(() => files.value.filter((file) => !currentBrand.value || String(file.brand_id || file.brand) === String(currentBrand.value.id || currentBrand.value.brand)));
const pages = computed(() => Math.max(1, Math.ceil(items.value.length / 20)));
const pageItems = computed(() => items.value.slice((page.value - 1) * 20, page.value * 20));
function dateTime(value) { return value ? new Date(value * 1000).toISOString() : undefined; }
function formatDate(value) { return value ? new Date(value * 1000).toLocaleDateString() : ""; }
function openFile(event, file) { if (event.target.closest("button, a, .ws-pconf") && !event.target.closest(".file-link")) return; router.push({ path: "/maya", query: { prompt: `Open my deliverable "${file.title}" (slug: ${file.slug}).` } }); }
function openFileByKey(event, file) { if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return; event.preventDefault(); openFile(event, file); }
function newArticle() { router.push({ path: "/maya", query: { prompt: "I want to write a new blog article." } }); }
function edit(file) { router.push({ path: "/maya", query: { article_edit: file.slug, article_q: file.title } }); }
function requestDelete(file) { confirmSlug.value = file.slug; }
async function load() { controller?.abort(); controller = new AbortController(); loading.value = true; error.value = ""; try { const data = await listDeliverables({ signal: controller.signal }); files.value = data.files || []; page.value = Math.min(page.value, pages.value); } catch (caught) { if (caught.name !== "AbortError") error.value = caught.message || "Unable to load deliverables."; } finally { loading.value = false; } }
async function publish(file) { busy.value = file.slug; publishedSlug.value = null; try { const result = await publishDeliverable(file.slug); publishedWas.value = !!file.published; publishedUrl.value = result?.url || ""; publishedSlug.value = file.slug; status.value = "Deliverable republished."; await load(); } catch (caught) { error.value = caught.message || "Unable to publish deliverable."; } finally { busy.value = null; } }
async function remove(file) { busy.value = file.slug; try { await deleteArticle({ brand: file.brand, slug: file.slug }); confirmSlug.value = null; status.value = "Deliverable deleted."; files.value = files.value.filter((item) => item.slug !== file.slug); } catch (caught) { error.value = caught.message || "Unable to delete deliverable."; } finally { busy.value = null; } }
onMounted(load); watch(() => currentBrand.value?.id, () => { page.value = 1; load(); }); onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.soa-view-files .pagehead h1 {
  margin-bottom: 8px;
}

.soa-view-files .rlog {
  padding-top: 10px;
}

.soa-view-files .pl-card h2 {
  margin: 0;
  font-size: var(--fs-subhead);
  font-weight: 700;
}

.file-openable {
  cursor: pointer;
}

.file-openable:focus-visible {
  outline: 3px solid var(--indigo);
  outline-offset: 2px;
}

.file-link {
  margin-left: 0;
  border: 0;
  padding: 0;
  background: transparent;
  font: inherit;
  color: var(--indigo);
  cursor: pointer;
}

.file-link:hover {
  text-decoration: underline;
}
</style>
